import { RemoveCircle } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";

export default function ArcadeConfig({
	gameConfig,
	setGameConfig,
	loading = false,
}: {
	gameConfig: ArcadeConfigType;
	setGameConfig: (config: ArcadeConfigType) => void;
	loading?: boolean;
}) {
	const computedMaxWin = useMemo(() => {
		const coinTiers = gameConfig?.tiers?.filter((t) => t.type === "coins") ?? [];
		if (!coinTiers.length) return 0;
		return Math.max(...coinTiers.map((t) => Number(t.won) || 0));
	}, [gameConfig?.tiers]);

	useEffect(() => {
		if (gameConfig?.maxWin !== computedMaxWin) {
			setGameConfig({ ...gameConfig, maxWin: computedMaxWin });
		}
	}, [computedMaxWin, gameConfig, setGameConfig]);

	const handleAddRow = () => {
		const newRow: ArcadeConfigType["tiers"][number] = {
			tier: gameConfig?.tiers?.length + 1 || 1,
			event: "",
			won: 0,
			chance: 1,
			type: "coins",
		};

		let newData: ArcadeConfigType["tiers"] = [];

		if (gameConfig?.tiers?.length) {
			newData = [...(gameConfig.tiers as ArcadeConfigType["tiers"]), newRow];
		} else {
			newData = [newRow];
		}

		setGameConfig({
			...gameConfig,
			tiers: newData,
		});
	};

	const handleDeleteRow = (rowIndex: number) => {
		if (gameConfig?.tiers.length <= 1) return;

		const newData = gameConfig?.tiers.filter((_: unknown, index: number) => index !== rowIndex);
		setGameConfig({
			...gameConfig,
			tiers: newData,
		});
	};

	const handleEntryChange = (entryIndex: number, name: string, value: any) => {
		const newEntries = [...gameConfig.tiers];
		newEntries[entryIndex] = { ...newEntries[entryIndex], [name]: value };

		setGameConfig({
			...gameConfig,
			tiers: newEntries,
		});
	};

	const validateChance = (e: React.ChangeEvent<HTMLInputElement>, prevChance: number) => {
		const value = Number(e.target.value);
		const max = Number(e.target.max);
		const min = Number(e.target.min);

		return value <= max && value >= min ? value : prevChance;
	};

	return (
		<Grid container spacing={3}>
			<Grid item xs={12}>
				<Box>
					<Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
						Entry Configuration
					</Typography>
					<Grid container sx={{ mb: 2 }} spacing={2}>
						<Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: 1 }}>
							<TextField
								label="Entry Fee"
								type="number"
								value={gameConfig?.entryFee || ""}
								onChange={(e) =>
									setGameConfig({
										...gameConfig,
										entryFee: Number(e.target.value),
									})
								}
								variant="outlined"
								fullWidth
								disabled={loading}
								sx={{
									"& .MuiOutlinedInput-root": {
										backgroundColor: "#ffffff",
										"&:hover": {
											backgroundColor: "#f8f9fa",
										},
										"&.Mui-focused": {
											backgroundColor: "#ffffff",
										},
									},
									"& .MuiInputLabel-root": {
										color: "#1976d2",
										fontWeight: 500,
									},
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: 1 }}>
							<TextField
								label="Default Tickets"
								type="number"
								value={gameConfig?.defaultTickets ?? 1}
								onChange={(e) =>
									setGameConfig({
										...gameConfig,
										defaultTickets: Number(e.target.value),
									})
								}
								variant="outlined"
								fullWidth
								disabled={loading}
								sx={{
									"& .MuiOutlinedInput-root": {
										backgroundColor: "#ffffff",
										"&:hover": {
											backgroundColor: "#f8f9fa",
										},
										"&.Mui-focused": {
											backgroundColor: "#ffffff",
										},
									},
									"& .MuiInputLabel-root": {
										color: "#1976d2",
										fontWeight: 500,
									},
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: 1 }}>
							<TextField
								label="Min Tickets"
								type="number"
								value={gameConfig?.minTickets ?? 1}
								onChange={(e) =>
									setGameConfig({
										...gameConfig,
										minTickets: Number(e.target.value),
									})
								}
								variant="outlined"
								fullWidth
								disabled={loading}
								sx={{
									"& .MuiOutlinedInput-root": {
										backgroundColor: "#ffffff",
										"&:hover": {
											backgroundColor: "#f8f9fa",
										},
										"&.Mui-focused": {
											backgroundColor: "#ffffff",
										},
									},
									"& .MuiInputLabel-root": {
										color: "#1976d2",
										fontWeight: 500,
									},
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: 1 }}>
							<TextField
								label="Max Tickets"
								type="number"
								value={gameConfig?.maxTickets ?? 1}
								onChange={(e) =>
									setGameConfig({
										...gameConfig,
										maxTickets: Number(e.target.value),
									})
								}
								variant="outlined"
								fullWidth
								disabled={loading}
								sx={{
									"& .MuiOutlinedInput-root": {
										backgroundColor: "#ffffff",
										"&:hover": {
											backgroundColor: "#f8f9fa",
										},
										"&.Mui-focused": {
											backgroundColor: "#ffffff",
										},
									},
									"& .MuiInputLabel-root": {
										color: "#1976d2",
										fontWeight: 500,
									},
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: 1 }}>
							<TextField
								label="Chance` (0% - 100%`)"
								type="number"
								value={gameConfig?.chance ?? ""}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									const validChance = validateChance(e, gameConfig?.chance || 1);

									setGameConfig({
										...gameConfig,
										chance: validChance,
									});
								}}
								inputProps={{
									min: 0,
									max: 100,
									step: 0.01,
								}}
								variant="outlined"
								fullWidth
								disabled={loading}
								sx={{
									"& .MuiOutlinedInput-root": {
										backgroundColor: "#ffffff",
										"&:hover": {
											backgroundColor: "#f8f9fa",
										},
										"&.Mui-focused": {
											backgroundColor: "#ffffff",
										},
									},
									"& .MuiInputLabel-root": {
										color: "#1976d2",
										fontWeight: 500,
									},
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: 1 }}>
							<TextField
								label="Max Win"
								type="number"
								value={computedMaxWin}
								variant="outlined"
								fullWidth
								disabled
								helperText="Highest 'won amount' across coin tiers"
								sx={{
									"& .MuiOutlinedInput-root": {
										backgroundColor: "#ffffff",
										"&:hover": {
											backgroundColor: "#f8f9fa",
										},
										"&.Mui-focused": {
											backgroundColor: "#ffffff",
										},
									},
									"& .MuiInputLabel-root": {
										color: "#1976d2",
										fontWeight: 500,
									},
								}}
							/>
						</Grid>
					</Grid>
				</Box>
				<Box>
					<Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
						Tiers Configuration
					</Typography>
					<Grid container>
						{gameConfig?.tiers?.map((_, index) => (
							<Grid container sx={{ mb: 2 }} spacing={1} alignContent={"center"} alignItems="center" key={`entry-row-${index}`}>
								<Grid item xs={2} key={`entry-tier-tier-${index}`}>
									<TextField
										fullWidth
										label={`Tier ${gameConfig.tiers?.[index]?.tier}`}
										value={gameConfig.tiers?.[index]?.tier || ""}
										onChange={(e) => handleEntryChange(index, "tier", e.target.value)}
										variant="outlined"
									/>
								</Grid>
								<Grid item xs={3} key={`entry-tier-event-${index}`}>
									<TextField
										fullWidth
										label={`Event Name ${gameConfig.tiers?.[index]?.tier}`}
										value={gameConfig.tiers?.[index]?.event || ""}
										onChange={(e) => handleEntryChange(index, "event", e.target.value)}
										variant="outlined"
										disabled={loading}
									/>
								</Grid>
								<Grid item xs={2} key={`entry-tier-chance-${index}`}>
									<TextField
										fullWidth
										label={`Chance ${gameConfig.tiers?.[index]?.tier} (0% - 100%)`}
										type="number"
										value={gameConfig?.tiers[index].chance ?? ""}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											const validChance = validateChance(e, gameConfig?.tiers[index]?.chance || 0);

											handleEntryChange(index, "chance", validChance);
										}}
										inputProps={{
											min: 0,
											max: 100,
											step: 0.01,
										}}
										variant="outlined"
										disabled={loading}
									/>
								</Grid>
								<Grid item xs={2} key={`entry-tier-won-${index}`}>
									<TextField
										fullWidth
										label={`Won amount ${gameConfig.tiers?.[index]?.tier}`}
										value={gameConfig.tiers?.[index]?.won || ""}
										onChange={(e) => handleEntryChange(index, "won", e.target.value)}
										variant="outlined"
										disabled={loading}
										sx={{
											"& .MuiOutlinedInput-root": {
												backgroundColor: "#ffffff",
												"&:hover": {
													backgroundColor: "#f8f9fa",
												},
												"&.Mui-focused": {
													backgroundColor: "#ffffff",
												},
											},
											"& .MuiInputLabel-root": {
												color: "#1976d2",
												fontWeight: 500,
											},
										}}
									/>
								</Grid>
								<Grid item xs={2} key={`entry-tier-type-${index}`}>
									<Select
										fullWidth
										value={gameConfig.tiers?.[index]?.type || "coins"}
										label="Entry Type"
										onChange={(e) => handleEntryChange(index, "type", e.target.value)}
									>
										<MenuItem value="coins">Coins</MenuItem>
										<MenuItem value="voucher">Voucher</MenuItem>
									</Select>
								</Grid>
								<Grid item key={`entry-tier-action-${index}`}>
									<IconButton color="error" onClick={() => handleDeleteRow(index)} disabled={gameConfig?.tiers.length <= 1}>
										<RemoveCircle />
									</IconButton>
								</Grid>
							</Grid>
						))}

						<Button variant="contained" color="primary" onClick={handleAddRow} disabled={loading}>
							Add Tier
						</Button>
					</Grid>
				</Box>
			</Grid>
		</Grid>
	);
}

export type ArcadeConfigType = {
	entryFee: number;
	chance: number;
	maxWin: number;
	defaultTickets: number;
	minTickets: number;
	maxTickets: number;
	tiers: {
		tier: number;
		chance: number;
		event: string;
		won: number;
		type: "coins" | "voucher";
	}[];
};
